import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Search, ChevronRight, ShieldAlert, PackageCheck } from "lucide-react";
import { formatRateChip } from "@/lib/rentalPricing";
import { FmcsaRiskChip } from "@/components/FmcsaRiskChip";

const STATUS_LABEL = {
    pending_contract: "Pending contract",
    contract_signed: "Contract signed",
    coi_pending: "Waiting for COI",
    coi_received: "COI received — review",
    coi_approved: "COI approved",
    active: "Active",
    ended: "Ended",
    terminated: "Terminated",
    scheduled: "Scheduled",
    imported: "Imported",
    // Phase RENTALS-SUBSTATUS-FILTER (2026-07-29) — sub-status
    // pseudo-values that translate to flag/embedded-field queries
    // on the backend (see routers/rentals.py::_SUBSTATUS_QUERIES).
    agent_uploaded: "Agent uploaded",
    ach_on_file: "ACH on file",
};

const STATUS_COLOR = {
    pending_contract: "bg-slate-100 text-slate-700",
    contract_signed: "bg-blue-50 text-blue-900",
    coi_pending: "bg-amber-50 text-amber-900",
    coi_received: "bg-violet-50 text-violet-800",
    coi_approved: "bg-emerald-50 text-emerald-800",
    active: "bg-emerald-100 text-emerald-900",
    ended: "bg-slate-200 text-slate-700",
    terminated: "bg-red-50 text-red-700",
    scheduled: "bg-blue-50 text-blue-800",
    imported: "bg-violet-50 text-violet-800",
    // Sub-status entries never appear as a MAIN pill (the underlying
    // rental doc doesn't carry these as ``status``) — added purely so
    // the dropdown can render a color when one is selected without
    // falling back to the neutral default.
    agent_uploaded: "bg-emerald-50 text-emerald-800",
    ach_on_file: "bg-violet-50 text-violet-800",
};

const EQUIPMENT_LABELS = {
    "": "All equipment", reefer: "Reefer", dry_van: "Dry Van", conestoga: "Conestoga",
    flatbed: "Flatbed", step_deck: "Step Deck", other: "Other",
};

// Phase RENTALS-BILLING-COL (2026-07-29) — dropdown values map 1:1 to
// the backend ``billing_status`` filter param. "stopped" aliases to
// "ended" on the backend (schema has no separate stopped state today);
// we still surface it as a filter option so future schema growth is
// discoverable from the UI.
const BILLING_FILTERS = [
    { value: "", label: "All billing" },
    { value: "recurring", label: "Recurring" },
    { value: "paused", label: "Paused" },
    { value: "stopped", label: "Stopped" },
    { value: "ended", label: "Ended" },
    { value: "none", label: "No schedule" },
];

// Public billing_status → chip visual. ``null`` renders as a muted
// em-dash (see resolveBillingChip).
const BILLING_TONE = {
    recurring: "bg-emerald-100 text-emerald-900 ring-emerald-200",
    paused: "bg-amber-50 text-amber-900 ring-amber-200",
    ended: "bg-slate-200 text-slate-700 ring-slate-300",
};
const BILLING_LABEL = {
    recurring: "Recurring", paused: "Paused", ended: "Ended",
};

function fmtDate(s) { if (!s) return "—"; return s.slice(0, 10); }

export default function AdminRentalsListPage() {
    const location = useLocation();
    const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const coiExpiring = urlParams.get("coi_expiring") === "1";
    const insuranceLapsed = urlParams.get("insurance_lapsed") === "1";
    const awaitingClosure = urlParams.get("awaiting_closure") === "1";
    const urlStatus = urlParams.get("status") || "";
    // Phase J.8c (2026-08-03) — cross-cutting saved-search filter. When
    // set, the list is intersected with the diagnostic aggregation from
    // /api/admin/rentals/uninvoiced-releases; a persistent banner + clear
    // button surfaces the filter to the operator.
    const savedFilter = urlParams.get("filter") || "";

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState(urlStatus);
    const [equipment, setEquipment] = useState("");
    // Phase QCM-EXT — FMCSA cached-snapshot risk filter on the rentals
    // grid. URL-synced via the same ``risk`` query param the customers
    // grid uses, so deep links work consistently between the two.
    const [riskFilter, setRiskFilter] = useState(urlParams.get("risk") || "");
    // Phase RENTALS-BILLING-COL (2026-07-29) — URL-synced billing filter.
    const [billingFilter, setBillingFilter] = useState(urlParams.get("billing") || "");

    const load = async () => {
        setLoading(true);
        try {
            const effectiveStatus = awaitingClosure ? "active" : status;
            const params = { q, status_filter: effectiveStatus, equipment_type: equipment, limit: 100 };
            if (coiExpiring) params.coi_expiring = 1;
            if (insuranceLapsed) params.insurance_lapsed = 1;
            if (riskFilter) params.risk = riskFilter;
            if (billingFilter) params.billing_status = billingFilter;
            if (savedFilter) params.filter = savedFilter;
            const { data } = await api.get("/admin/rentals", { params });
            let rows = data.items || [];
            let totalCount = data.total || 0;
            if (awaitingClosure) {
                rows = rows.filter((r) => r.status === "active" && !!r.return_recorded_at);
                totalCount = rows.length;
            }
            setItems(rows);
            setTotal(totalCount);
        } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, equipment, coiExpiring, insuranceLapsed, awaitingClosure, riskFilter, billingFilter, savedFilter]);

    const onSearch = (e) => { e.preventDefault(); load(); };

    return (
        <DashboardShell>
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Operations</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Rentals</h1>
                    <p className="text-sm text-slate-500 mt-1">Cross-customer pipeline of equipment rentals and their COI status.</p>
                </div>
                <div className="text-right">
                    <p className="text-3xl font-display font-bold text-slate-900" data-testid="rentals-total">{total}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total rentals</p>
                </div>
            </div>

            {/* Phase J.8c (2026-08-03) — persistent banner + clear button
                when the "released without invoice" saved-search filter is
                active. Deep-linkable via ?filter=uninvoiced_releases. */}
            {savedFilter === "uninvoiced_releases" && (
                <div
                    data-testid="rentals-uninvoiced-releases-banner"
                    className="mt-6 flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3"
                >
                    <div className="flex items-center gap-2">
                        <ShieldAlert size={16} className="text-red-700" />
                        <p className="text-sm text-red-900">
                            <span className="font-semibold">Filtered:</span> Released without invoice ·{" "}
                            <span data-testid="rentals-uninvoiced-releases-banner-count">{total}</span>{" "}
                            rental{total === 1 ? "" : "s"}
                        </p>
                    </div>
                    <Link
                        to="/admin/rentals"
                        data-testid="rentals-uninvoiced-releases-clear"
                        className="text-xs font-semibold text-red-800 underline hover:text-red-900"
                    >
                        ✕ Clear filter
                    </Link>
                </div>
            )}

            <Card className="mt-6">
                <CardContent className="p-4 flex flex-wrap gap-3 items-center">
                    {coiExpiring && (
                        <Link
                            to="/admin/rentals"
                            data-testid="rentals-coi-expiring-chip"
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-900 ring-1 ring-amber-200 px-3 py-1 text-xs font-semibold hover:bg-amber-100 transition-colors"
                            title="Clear filter"
                        >
                            <ShieldAlert size={12} /> COI expiring ≤30 days · clear
                        </Link>
                    )}
                    {/* Phase COI-REFRESH (Part B) — Lapsed insurance filter chip. */}
                    {insuranceLapsed ? (
                        <Link
                            to="/admin/rentals"
                            data-testid="rentals-insurance-lapsed-chip"
                            aria-pressed="true"
                            className="inline-flex items-center gap-1.5 rounded-full bg-red-100 text-red-800 ring-1 ring-red-300 px-3 py-1 text-xs font-semibold hover:bg-red-200 transition-colors"
                            title="Clear filter"
                        >
                            <ShieldAlert size={12} /> Lapsed insurance · clear
                        </Link>
                    ) : (
                        <Link
                            to="/admin/rentals?insurance_lapsed=1"
                            data-testid="rentals-insurance-lapsed-chip"
                            aria-pressed="false"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-700 ring-1 ring-slate-200 px-3 py-1 text-xs font-semibold hover:bg-red-50 hover:text-red-700 hover:ring-red-200 transition-colors"
                            title="Show rentals whose COI has lapsed past the grace window"
                        >
                            <ShieldAlert size={12} /> Lapsed insurance
                        </Link>
                    )}
                    {awaitingClosure ? (
                        <Link
                            to="/admin/rentals"
                            data-testid="rental-filter-awaiting-closure"
                            aria-pressed="true"
                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300 px-3 py-1 text-xs font-semibold hover:bg-emerald-200 transition-colors"
                            title="Clear filter"
                        >
                            <PackageCheck size={12} /> Awaiting closure · clear
                        </Link>
                    ) : (
                        <Link
                            to="/admin/rentals?awaiting_closure=1"
                            data-testid="rental-filter-awaiting-closure"
                            aria-pressed="false"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-700 ring-1 ring-slate-200 px-3 py-1 text-xs font-semibold hover:bg-slate-50 hover:text-slate-900 transition-colors"
                            title="Show only rentals with a manager-recorded return, awaiting admin closure"
                        >
                            <PackageCheck size={12} /> Awaiting closure
                        </Link>
                    )}
                    <form onSubmit={onSearch} className="flex-1 min-w-[200px] flex gap-2">
                        <div className="relative flex-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search unit #, VIN, plate, make/model"
                                className="pl-9" data-testid="rentals-search-input" />
                        </div>
                        <Button type="submit" variant="outline" data-testid="rentals-search-go">Search</Button>
                    </form>
                    <select value={status} onChange={(e) => setStatus(e.target.value)}
                        className="flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        data-testid="rentals-status-filter">
                        <option value="">All statuses</option>
                        {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    {/* Phase RENTALS-BILLING-COL (2026-07-29) — All billing dropdown. */}
                    <select
                        value={billingFilter}
                        onChange={(e) => {
                            const next = e.target.value;
                            setBillingFilter(next);
                            const q2 = new URLSearchParams(location.search);
                            if (!next) q2.delete("billing"); else q2.set("billing", next);
                            const qs = q2.toString();
                            window.history.replaceState(null, "", qs ? `?${qs}` : location.pathname);
                        }}
                        className="flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        data-testid="rentals-billing-filter"
                    >
                        {BILLING_FILTERS.map((b) => (
                            <option key={b.value || "all"} value={b.value}>{b.label}</option>
                        ))}
                    </select>
                    <select value={equipment} onChange={(e) => setEquipment(e.target.value)}
                        className="flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        data-testid="rentals-equipment-filter">
                        {Object.entries(EQUIPMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardContent className="p-0">
                    {loading && <p className="text-sm text-slate-500 p-6" data-testid="rentals-loading">Loading…</p>}
                    {!loading && items.length === 0 && (
                        <div className="p-12 text-center" data-testid="rentals-empty">
                            <div className="mx-auto h-12 w-12 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400">
                                <Truck size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">No rentals match</h3>
                            <p className="mt-2 text-sm text-slate-600">Try removing filters, or send a contract for signature to spin up the first rental.</p>
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <table className="min-w-full text-sm">
                            <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                                <tr>
                                    <th className="text-left py-3 px-4">Customer</th>
                                    <th className="text-left py-3 px-4">Equipment</th>
                                    <th className="text-left py-3 px-4">Unit</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                    <th className="text-left py-3 px-4">Billing</th>
                                    <th className="text-left py-3 px-4">Start</th>
                                    <th className="text-left py-3 px-4">End</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((r) => (
                                    <tr key={r.id} data-testid={`rentals-row-${r.id}`} className="hover:bg-slate-50/50">
                                        <td className="py-3 px-4">
                                            <Link to={`/admin/customers/${r.customer_id}`} className="text-blue-900 hover:underline font-medium">
                                                {r.company_name || "—"}
                                            </Link>
                                            {r.dba && <div className="text-xs text-slate-500">DBA {r.dba}</div>}
                                        </td>
                                        <td className="py-3 px-4 capitalize text-slate-700">
                                            {(r.equipment_type || "").replace("_", " ") || "—"}
                                            {formatRateChip(r) && (
                                                <div className="text-[11px] text-slate-500 normal-case font-mono mt-0.5"
                                                    data-testid={`rentals-rate-${r.id}`}>{formatRateChip(r)}</div>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-slate-700">{r.unit_number || "—"}</td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[r.status] || "bg-slate-100"}`}>
                                                    {STATUS_LABEL[r.status] || r.status}
                                                </span>
                                                {/*
                                                 * Phase RENTALS-STATUS-BADGES (2026-07-29) — sub-status
                                                 * badges are hidden by default. Each one only appears
                                                 * when its corresponding sub-status filter is ACTIVE
                                                 * AND the row still carries the underlying flag. This
                                                 * keeps the Status column clean while preserving the
                                                 * filter → visual-reinforcement pairing.
                                                 *
                                                 * The three sub-status filters ("imported",
                                                 * "agent_uploaded", "ach_on_file") each light up
                                                 * their own pill; the "imported" pill is suppressed
                                                 * when the main status already reads "Imported" so
                                                 * we don't render the same word twice.
                                                 */}
                                                {status === "imported" && r.imported && r.status !== "imported" && (
                                                    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-violet-50 text-violet-800 ring-1 ring-violet-200"
                                                        data-testid={`rentals-imported-${r.id}`}>Imported</span>
                                                )}
                                                {status === "agent_uploaded" && (r.coi_request || {}).response_source === "agent_upload" && (
                                                    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                                                        data-testid={`rentals-agent-uploaded-${r.id}`}>Agent uploaded</span>
                                                )}
                                                {status === "ach_on_file" && r.ach_form_imported && (
                                                    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-violet-50 text-violet-800 ring-1 ring-violet-200"
                                                        title={`ACH on file · ${r.ach_form_imported.filename || ""}`}
                                                        data-testid={`rentals-ach-on-file-${r.id}`}>ACH on file</span>
                                                )}
                                                {/* Phase J.4c (2026-07-29) — ACH activation status. */}
                                                {r.ach_activation_status === "sent" && (
                                                    <span
                                                        data-testid={`rentals-ach-activation-chip-${r.id}`}
                                                        title="Customer sent an ACH activation link — awaiting verification"
                                                        className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-900 ring-1 ring-amber-200"
                                                    >ACH link sent</span>
                                                )}
                                                {r.ach_activation_status === "expired" && (
                                                    <span
                                                        data-testid={`rentals-ach-activation-chip-${r.id}`}
                                                        title="ACH activation link expired — resend from customer detail"
                                                        className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-red-50 text-red-800 ring-1 ring-red-200"
                                                    >ACH link expired</span>
                                                )}
                                                {r.ach_activation_status === "verified" && (
                                                    <span
                                                        data-testid={`rentals-ach-activation-chip-${r.id}`}
                                                        title="Customer bank verified via Stripe Financial Connections"
                                                        className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                                                    >ACH verified</span>
                                                )}
                                                {/* Phase QCM-EXT — FMCSA risk chip (renders nothing when no risk). */}
                                                <FmcsaRiskChip
                                                    risk={r.fmcsa_risk}
                                                    testId={`rentals-fmcsa-risk-chip-${r.id}`}
                                                />
                                            </div>
                                        </td>
                                        {/* Phase RENTALS-BILLING-COL (2026-07-29) — Billing chip. */}
                                        <td className="py-3 px-4">
                                            {r.billing_status ? (
                                                <span
                                                    data-testid={`rentals-billing-chip-${r.id}`}
                                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${BILLING_TONE[r.billing_status] || "bg-slate-100 text-slate-700 ring-slate-200"}`}
                                                >
                                                    {BILLING_LABEL[r.billing_status] || r.billing_status}
                                                </span>
                                            ) : (
                                                <span
                                                    data-testid={`rentals-billing-none-${r.id}`}
                                                    className="text-slate-400 text-sm"
                                                    title="No billing schedule — manual invoicing only"
                                                >—</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-slate-600">{fmtDate(r.start_date)}</td>
                                        <td className="py-3 px-4 text-slate-600">{fmtDate(r.end_date) || "open"}</td>
                                        <td className="py-3 px-4 text-right">
                                            <Button asChild size="sm" variant="outline" className="gap-1.5">
                                                <Link to={`/admin/rentals/${r.id}`} data-testid={`rentals-open-${r.id}`}>
                                                    Open <ChevronRight size={14} />
                                                </Link>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </DashboardShell>
    );
}
